import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import { AnalyticsService } from './analytics.service';
import {
  buildRouteName,
  currentLocationPath,
  sanitizePath,
} from './rum-route.util';

/** Prefix used for the long-task metric names reported to the analytics backend. */
export const LONG_TASKS_METRIC_PREFIX = 'LongTasks';

/** Name of the custom event emitted with the aggregated long-task summary. */
export const LONG_TASKS_EVENT_NAME = 'LongTasks';

/** A long task is, by spec, any task that blocks the main thread for >= 50ms. */
const LONG_TASK_THRESHOLD_MS = 50;

/**
 * Upper sanity bound for a single long task. A genuine synchronous main-thread
 * block never approaches this in practice; a "long task" longer than this is a
 * device-sleep / tab-suspend artifact — when the machine sleeps or the tab is
 * backgrounded mid-measurement, the browser reports the whole wall-clock gap as
 * one `longtask` entry. Such entries are dropped (see {@link record}) so they do
 * not inflate `maxDuration`, `totalBlockedMs`, or the over-N freeze counters.
 */
const MAX_PLAUSIBLE_LONG_TASK_MS = 60_000;

/**
 * Running aggregate of the long tasks seen during a single visibility period,
 * flushed (and reset) when the page is hidden or unloaded.
 */
interface LongTaskAggregate {
  count: number;
  maxDuration: number;
  totalBlockedMs: number;
  over1000: number;
  over3000: number;
}

/**
 * Non-standard browser hints that are widely available but not in the lib DOM
 * typings. Treated as fully optional because support varies by browser.
 */
interface BrowserPressureSource {
  deviceMemory?: number;
  hardwareConcurrency?: number;
  connection?: { effectiveType?: string };
}

/**
 * Observes browser **long tasks** (main-thread blocking >= 50ms) and reports an
 * aggregated summary per visibility period to the analytics backend.
 *
 * Long tasks are the primary browser-native signal for UI freezes and jank, so
 * this directly backs the "jitters and freezes" investigation. Raw long-task
 * entries are high frequency, so they are aggregated and a single summary is
 * sent when the page is hidden or unloaded (one event per page/session period,
 * not one per task) to keep telemetry volume under control.
 *
 * Mirrors {@link WebVitalsService}: each flush emits a numeric metric
 * (`LongTasks/MaxDuration`, for percentile aggregation) plus a custom event
 * (`LongTasks`, carrying the full aggregate and safe browser-pressure hints so
 * it can be sliced in dashboards).
 *
 * Attribution is **per route**: the aggregate is flushed (and reset) on each
 * Angular Router `NavigationStart`, so each summary belongs to the single route
 * the user was actually on while the long tasks happened. The Router is injected
 * optionally — without it the service still works, falling back to flushing only
 * on page hide/unload (one summary per visibility period).
 */
@Injectable()
export class LongTasksService {
  private analytics = inject(AnalyticsService);

  private router = inject(Router, { optional: true });

  private initialized = false;

  private observer: PerformanceObserver | null = null;

  private aggregate = LongTasksService.emptyAggregate();

  /**
   * Path (with query string) of the route the long tasks are currently being
   * attributed to. Query params are kept on purpose — sort/filter/paging state
   * often explains a data-heavy freeze — but sensitive params are redacted via
   * {@link scrubUrl} and the fragment is dropped. Tracked explicitly (updated on
   * `NavigationEnd`) rather than read from `window.location` at flush time, so
   * attribution stays correct even for back/forward navigations where the URL
   * changes before `NavigationStart`.
   */
  private currentRoute = '';

  /**
   * Stable route template for {@link currentRoute} (e.g. `/orders/:id`), derived
   * from the matched Angular route config. Low cardinality, so it groups cleanly
   * in dashboards; the raw `currentRoute` keeps the concrete value (e.g. the
   * order id) for drill-down. Empty when no Router is available.
   */
  private currentRouteName = '';

  /**
   * Starts observing long tasks. Safe to call multiple times; only the first
   * call registers the observer. No-ops when the browser does not support the
   * `longtask` PerformanceObserver entry type.
   */
  public init(): void {
    if (this.initialized || !this.isSupported()) {
      return;
    }
    this.initialized = true;
    this.currentRoute = sanitizePath(currentLocationPath());

    this.observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => this.record(entry.duration));
    });
    this.observer.observe({ entryTypes: ['longtask'] });

    // Per-route attribution: close off the current route's window on each SPA
    // navigation, and remember the new route once it resolves so the next
    // window is attributed correctly.
    this.router?.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.flush();
      } else if (event instanceof NavigationEnd) {
        this.currentRoute = sanitizePath(event.urlAfterRedirects);
        this.currentRouteName = buildRouteName(this.router);
      }
    });

    // Also flush when the user navigates away or backgrounds the tab. `pagehide`
    // is the most reliable terminal signal; `visibilitychange` catches tab
    // switches that never fire an unload. This is the only flush trigger when no
    // Router is present. `visibilitychange` is dispatched on `document`, not
    // `window`, so it must be registered there.
    window.addEventListener('pagehide', () => this.flush());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });
  }

  /**
   * Returns true when the runtime can observe long tasks. Guards both the
   * absence of `PerformanceObserver` (older browsers, non-DOM test envs) and
   * the absence of `longtask` support (e.g. Safari/Firefox).
   */
  private isSupported(): boolean {
    return (
      typeof PerformanceObserver !== 'undefined' &&
      Array.isArray(PerformanceObserver.supportedEntryTypes) &&
      PerformanceObserver.supportedEntryTypes.includes('longtask')
    );
  }

  /**
   * Folds a single long-task duration into the running aggregate.
   * @param duration
   */
  private record(duration: number): void {
    if (duration > MAX_PLAUSIBLE_LONG_TASK_MS) {
      // Suspend/sleep artifact, not a real freeze — drop it entirely so it
      // pollutes none of the aggregates below.
      return;
    }

    const agg = this.aggregate;
    agg.count += 1;
    agg.maxDuration = Math.max(agg.maxDuration, duration);
    agg.totalBlockedMs += Math.max(0, duration - LONG_TASK_THRESHOLD_MS);
    if (duration >= 1000) {
      agg.over1000 += 1;
    }
    if (duration >= 3000) {
      agg.over3000 += 1;
    }
  }

  /**
   * Emits the accumulated long-task summary (if any) and resets the aggregate
   * for the next visibility period. Does nothing when no long tasks were seen.
   */
  private flush(): void {
    const agg = this.aggregate;
    if (agg.count === 0) {
      return;
    }
    this.aggregate = LongTasksService.emptyAggregate();

    const properties = {
      longTaskCount: agg.count,
      maxLongTaskDuration: agg.maxDuration,
      totalBlockedMs: agg.totalBlockedMs,
      longTasksOver1000Ms: agg.over1000,
      longTasksOver3000Ms: agg.over3000,
      page: this.currentRoute,
      routeName: this.currentRouteName,
      ...this.pressureHints(),
    };

    this.analytics.trackMetric({
      name: `${LONG_TASKS_METRIC_PREFIX}/MaxDuration`,
      average: agg.maxDuration,
      properties,
    });

    this.analytics.trackEvent({
      name: LONG_TASKS_EVENT_NAME,
      properties,
    });
  }

  /**
   * Collects safe, non-sensitive browser-pressure hints to help separate
   * device/network constraints from app-side jank. Every field is optional
   * because browser support varies.
   */
  private pressureHints(): Record<string, string | number> {
    const hints: Record<string, string | number> = {};
    const nav = navigator as Navigator & BrowserPressureSource;

    if (typeof nav.deviceMemory === 'number') {
      hints['deviceMemory'] = nav.deviceMemory;
    }
    if (typeof nav.hardwareConcurrency === 'number') {
      hints['hardwareConcurrency'] = nav.hardwareConcurrency;
    }
    if (nav.connection?.effectiveType) {
      hints['effectiveType'] = nav.connection.effectiveType;
    }
    if (typeof document !== 'undefined' && document.visibilityState) {
      hints['visibilityState'] = document.visibilityState;
    }

    return hints;
  }

  /**
   * Returns a zeroed aggregate.
   */
  private static emptyAggregate(): LongTaskAggregate {
    return {
      count: 0,
      maxDuration: 0,
      totalBlockedMs: 0,
      over1000: 0,
      over3000: 0,
    };
  }
}
